import React, { useState, useEffect, useMemo } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { getAllDatasetVersions, updateDatasetVersion } from '../../services/adminDatasetService';
import { useNotification } from '../../components/framework/NotificationContext';

const DatasetManagement = () => {
    const { success, error: notifyError } = useNotification();
    
    // View mode: 'holistic' (default overview) | 'manage' (list and edit datasets)
    const [viewMode, setViewMode] = useState('holistic');
    
    const [versions, setVersions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Filtering for manage mode
    const [searchTerm, setSearchTerm] = useState('');
    const [filterDoi, setFilterDoi] = useState('all'); // 'all', 'with_doi', 'without_doi'
    
    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [currentVersion, setCurrentVersion] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        loadVersions();
    }, []);

    const loadVersions = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getAllDatasetVersions();
            setVersions(data || []);
        } catch (err) {
            setError(err.message || 'Failed to fetch dataset versions');
        } finally {
            setLoading(false);
        }
    };

    const openEditModal = (version) => {
        setCurrentVersion(version);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setCurrentVersion(null);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        const doi = formData.get('doi');
        const description = formData.get('description');
        const license = formData.get('license');

        setSaving(true);
        try {
            const updated = await updateDatasetVersion(currentVersion.id, { doi, description, license });
            setVersions(prev => prev.map(v => v.id === updated.id ? updated : v));
            success('Dataset release updated successfully');
            closeModal();
        } catch (err) {
            notifyError(err.message || 'Failed to update dataset release');
        } finally {
            setSaving(false);
        }
    };

    // Filtered versions for Manage mode
    const filteredVersions = useMemo(() => {
        return versions.filter(v => {
            const term = searchTerm.toLowerCase().trim();
            const matchesSearch = !term || 
                (v.versionIdentifier && v.versionIdentifier.toLowerCase().includes(term)) ||
                (v.doi && v.doi.toLowerCase().includes(term)) ||
                (v.description && v.description.toLowerCase().includes(term));

            const hasDoi = v.doi && v.doi.trim() !== '';
            let matchesDoi = true;
            if (filterDoi === 'with_doi') matchesDoi = hasDoi;
            if (filterDoi === 'without_doi') matchesDoi = !hasDoi;

            return matchesSearch && matchesDoi;
        });
    }, [versions, searchTerm, filterDoi]);

    return (
        <AdminLayout
            title="Dataset Management"
            subtitle={
                viewMode === 'holistic'
                    ? "Holistic view of published datasets."
                    : "Manage existing dataset releases and edit details."
            }
        >
            {error && <div className="alert alert-danger" style={{ borderRadius: '8px', marginBottom: '1.5rem' }}>{error}</div>}

            {/* HOLISTIC VIEW LEVEL */}
            {viewMode === 'holistic' && (
                <div className="admin-card text-center py-5" style={{ maxWidth: '600px', margin: '2rem auto' }}>
                    <div style={{ fontSize: '3.5rem', color: '#003366', marginBottom: '1rem' }}>
                        <i className="bi bi-database"></i>
                    </div>
                    <h3 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#002244', marginBottom: '0.5rem' }}>
                        Published Datasets
                    </h3>
                    <div style={{ fontSize: '3rem', fontWeight: '800', color: '#003366', margin: '1rem 0' }}>
                        {loading ? '...' : versions.length}
                    </div>
                    <p style={{ color: '#6c757d', marginBottom: '2rem' }}>
                        Total number of dataset releases published in the system.
                    </p>
                    <button
                        className="btn btn-primary btn-lg px-4"
                        onClick={() => setViewMode('manage')}
                        style={{ borderRadius: '8px', fontWeight: '600' }}
                    >
                        <i className="bi bi-sliders me-2"></i> Manage Existing Datasets
                    </button>
                </div>
            )}

            {/* MANAGE EXISTING DATASETS LEVEL */}
            {viewMode === 'manage' && (
                <div className="dataset-manage-container">
                    {/* Breadcrumb / Top Bar */}
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <div className="dataset-breadcrumb" style={{ margin: 0 }}>
                            <span className="dataset-breadcrumb-link" onClick={() => setViewMode('holistic')}>
                                <i className="bi bi-grid-1x2-fill me-1"></i> Holistic View
                            </span>
                            <i className="bi bi-chevron-right"></i>
                            <span>Manage Existing Datasets</span>
                        </div>
                        <button className="btn btn-outline-primary btn-sm" onClick={() => setViewMode('holistic')} style={{ borderRadius: '6px' }}>
                            ← Back to Holistic View
                        </button>
                    </div>

                    {/* Toolbar: Search, Filters, Refresh */}
                    <div className="dataset-search-toolbar">
                        <div className="dataset-search-box">
                            <i className="bi bi-search"></i>
                            <input
                                type="text"
                                className="dataset-search-input"
                                placeholder="Search by version, DOI, or description..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        <div className="d-flex align-items-center gap-2">
                            <select
                                className="dataset-filter-select"
                                value={filterDoi}
                                onChange={(e) => setFilterDoi(e.target.value)}
                            >
                                <option value="all">All Releases</option>
                                <option value="with_doi">With DOI</option>
                                <option value="without_doi">Missing DOI</option>
                            </select>

                            <button className="btn btn-outline-secondary" onClick={loadVersions} style={{ borderRadius: '8px' }}>
                                <i className="bi bi-arrow-clockwise me-1"></i> Refresh
                            </button>
                        </div>
                    </div>

                    {/* Datasets Table */}
                    <div className="admin-card">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <span style={{ color: '#64748b', fontWeight: '500' }}>
                                {loading ? 'Loading...' : `Showing ${filteredVersions.length} of ${versions.length} dataset release${versions.length === 1 ? '' : 's'}`}
                            </span>
                        </div>

                        {loading ? (
                            <div style={{ textAlign: 'center', padding: '3rem' }}>
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p style={{ marginTop: '1rem', color: '#6c757d' }}>Loading dataset releases...</p>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="admin-table">
                                    <thead>
                                        <tr>
                                            <th>Version</th>
                                            <th>Release Date</th>
                                            <th>Records</th>
                                            <th>DOI</th>
                                            <th>License</th>
                                            <th>Description</th>
                                            <th>Artifacts</th>
                                            <th style={{ textAlign: 'right' }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredVersions.length === 0 ? (
                                            <tr>
                                                <td colSpan="8" style={{ textAlign: 'center', padding: '3rem', color: '#6c757d' }}>
                                                    <i className="bi bi-database-x" style={{ fontSize: '2rem', display: 'block', marginBottom: '0.5rem' }}></i>
                                                    No matching dataset releases found.
                                                </td>
                                            </tr>
                                        ) : (
                                            filteredVersions.map(version => (
                                                <tr key={version.id}>
                                                    <td style={{ fontWeight: 600, color: '#002244' }}>
                                                        {version.versionIdentifier}
                                                    </td>
                                                    <td className="mono-font" style={{ fontSize: '0.9rem' }}>
                                                        {version.createdAt ? new Date(version.createdAt).toLocaleDateString('en-US', {
                                                            year: 'numeric', month: 'short', day: 'numeric'
                                                        }) : '-'}
                                                    </td>
                                                    <td>{version.recordCount}</td>
                                                    <td>
                                                        {version.doi ? (
                                                            <a href={`https://doi.org/${version.doi}`} target="_blank" rel="noopener noreferrer">
                                                                {version.doi}
                                                            </a>
                                                        ) : (
                                                            <span className="badge bg-warning-subtle text-warning" style={{ border: '1px solid currentColor' }}>
                                                                Not set
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {version.license ? (
                                                            <span className="badge bg-light text-dark border">{version.license}</span>
                                                        ) : (
                                                            <span style={{ color: '#94a3b8' }}>—</span>
                                                        )}
                                                    </td>
                                                    <td style={{ maxWidth: '280px' }}>
                                                        {version.description ? (
                                                            <span title={version.description} style={{
                                                                display: 'block',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap'
                                                            }}>
                                                                {version.description}
                                                            </span>
                                                        ) : (
                                                            <span style={{ color: '#6c757d' }}>No description</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <div style={{ display: 'flex', gap: '0.35rem' }}>
                                                            <span className={`badge ${version.hasJson ? 'bg-success-subtle text-success' : 'bg-secondary-subtle text-secondary'}`} title="JSON">JSON</span>
                                                            <span className={`badge ${version.hasExcel ? 'bg-success-subtle text-success' : 'bg-secondary-subtle text-secondary'}`} title="Excel">XLSX</span>
                                                            <span className={`badge ${version.hasImages ? 'bg-success-subtle text-success' : 'bg-secondary-subtle text-secondary'}`} title="Images">IMG</span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                                                            <button
                                                                className="btn btn-sm btn-outline-primary"
                                                                onClick={() => openEditModal(version)}
                                                                title="Edit DOI, license and description"
                                                            >
                                                                <i className="bi bi-pencil"></i> Edit
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* EDIT DATASET RELEASE MODAL */}
            {showModal && currentVersion && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        zIndex: 9999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <div
                        style={{
                            backgroundColor: '#ffffff',
                            padding: '24px',
                            borderRadius: '12px',
                            width: '600px',
                            maxWidth: '92vw',
                            boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '16px',
                            maxHeight: '90vh',
                            overflowY: 'auto'
                        }}
                    >
                        <form onSubmit={handleSave}>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h3 style={{ margin: 0, fontWeight: '700', fontSize: '20px', color: '#002244' }}>
                                    Edit Release &mdash; {currentVersion.versionIdentifier}
                                </h3>
                                <button type="button" className="btn-close" onClick={closeModal}></button>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    <label style={{ fontSize: '14px', fontWeight: '600', color: '#334155' }}>DOI (Digital Object Identifier)</label>
                                    <input
                                        name="doi"
                                        defaultValue={currentVersion.doi || ''}
                                        className="form-control"
                                        placeholder="e.g. 10.5281/zenodo.1234567"
                                        style={{ padding: '10px 12px', fontSize: '14px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                                    />
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    <label style={{ fontSize: '14px', fontWeight: '600', color: '#334155' }}>License</label>
                                    <input
                                        name="license"
                                        defaultValue={currentVersion.license || ''}
                                        className="form-control"
                                        placeholder="e.g. CC BY 4.0, MIT, Open Data Commons"
                                        style={{ padding: '10px 12px', fontSize: '14px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                                    />
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    <label style={{ fontSize: '14px', fontWeight: '600', color: '#334155' }}>Release Description</label>
                                    <textarea
                                        name="description"
                                        defaultValue={currentVersion.description || ''}
                                        className="form-control"
                                        rows={5}
                                        placeholder="Describe what changed in this release..."
                                        style={{ padding: '10px 12px', fontSize: '14px', borderRadius: '6px', border: '1px solid #cbd5e1', resize: 'vertical' }}
                                    />
                                </div>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '24px' }}>
                                <button type="button" className="btn btn-light" onClick={closeModal} disabled={saving} style={{ borderRadius: '6px' }}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary" disabled={saving} style={{ borderRadius: '6px', fontWeight: '600' }}>
                                    {saving ? 'Saving...' : 'Save Changes'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
};

export default DatasetManagement;
